// ActivityCard — one card face for vsj.activities.<key> (craft, extraction,
// or hunt). Clicking the face selects it into the info pane and (craft only)
// opens the bench via select_card. Quick buttons: ∞ (start_inf) and 1
// (start_one); a dead button NAMES its blocker. Constant width per context.

import { clear, el, fmtDur, kbtn, tooltip } from '../kit';
import * as icons from '../icons';
import * as ui from '../ui';
import * as vsj from '../vsj';
import { STATS, statChip } from '../statvocab';

export interface CardHandle {
  root: HTMLElement;
  update(key: string, data: any, glow: string[]): void;
}

export function activityCard(location = false, nameColorVar = ''): CardHandle {
  const root = el('div', { class: 'k-panel raised activity-card' + (location ? ' location' : '') });
  let sig = '';
  let key = '';
  let data: any = {};

  root.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('button, .c-link, .c-creature[data-info]')) return;
    if (data.locked) return;
    ui.select('tpl_' + key);
    if (String(data.kind ?? '') === 'craft')
      import('../net').then((m) => m.sendEvent('select_card', { template: key }));
  });

  const buildLocked = () => {
    root.style.cursor = 'default';
    root.append(
      el(
        'div',
        { class: 'head' },
        icons.silhouette(String(data.icon ?? 'rock'), 40),
        el(
          'div',
          {},
          el('div', { class: 'k-faint', text: '???' }),
          el('div', { class: 'k-small k-faint', text: `Lv ${data.locked.level ?? '?'}` }),
        ),
      ),
    );
  };

  const buildFace = (glow: string[]) => {
    root.style.cursor = 'pointer';
    // header: icon | name + time | xp chip
    const nameCol = el('div', { style: 'flex:1;min-width:0' });
    nameCol.append(
      el('div', {
        style:
          'overflow:hidden;text-overflow:ellipsis;white-space:nowrap' +
          (nameColorVar ? `;color:${nameColorVar}` : ''),
        text: String(data.name ?? key),
      }),
    );
    const baseS = data.base_s, realS = data.real_s;
    if (baseS != null) {
      const txt =
        realS != null && Math.abs(Number(realS) - Number(baseS)) > 0.05
          ? `${fmtDur(Number(realS))} (base ${fmtDur(Number(baseS))})`
          : fmtDur(Number(baseS));
      nameCol.append(el('div', { class: 'k-small k-dim', text: txt }));
    }
    const head = el('div', { class: 'head' }, icons.plated(String(data.icon ?? 'rock'), 30, 4), nameCol);
    if (data.you_level != null) {
      // your level in this card's skill (extraction cards ship it)
      const skillName = String(data.skill ?? 'skill');
      const lvlChip = el('span', {
        class: 'k-chip',
        style: 'border-color:var(--c-skill)',
        text: `Lv ${data.you_level}`,
      });
      tooltip(lvlChip, () => `Your ${skillName[0].toUpperCase() + skillName.slice(1)} level`);
      head.append(lvlChip);
    }
    if (data.xp != null)
      head.append(el('span', { class: 'k-chip', style: 'border-color:var(--bar-xp)', text: `+${data.xp} xp` }));
    root.append(head);

    // served speed verdict: your speed multiplier + when the next step lands
    if (data.speed_mult != null) {
      let spTxt = `×${data.speed_mult} speed`;
      if (data.next_speed_at != null) spTxt += ` — next at Lv ${data.next_speed_at}`;
      const sp = el('span', { class: 'k-chip', style: 'align-self:flex-start', text: spTxt });
      tooltip(sp, () => 'Your level speeds this up');
      root.append(sp);
    }

    // served extra-xp list (e.g. mining also trains str) — statvocab chip
    // where the served skill matches a stat key, plain chip otherwise
    if (Array.isArray(data.xp_also) && data.xp_also.length) {
      const xr = el('div', { class: 'stat-row' });
      for (const x of data.xp_also) {
        if (!x || typeof x !== 'object') continue;
        const skill = String(x.skill ?? x.stat ?? '');
        const amt = x.xp ?? '?';
        if (skill in STATS) xr.append(statChip(skill, `+${amt}`));
        else xr.append(el('span', { class: 'k-chip', text: `+${amt} ${skill}`.trim() }));
      }
      if (xr.childElementCount) root.append(xr);
    }

    // recipe row
    if (Array.isArray(data.recipe_row) && data.recipe_row.length) {
      const rr = el('div', { class: 'recipe-row' });
      for (const ing of data.recipe_row) {
        if (!ing || typeof ing !== 'object') continue;
        const short = ing.short === true; // served verdict
        rr.append(
          el(
            'span',
            { style: 'display:inline-flex;gap:2px;align-items:center' },
            icons.texture(String(ing.item ?? ''), 18),
            el('span', {
              class: short ? 'short' : 'k-dim',
              text:
                `×${ing.count ?? '?'} (${ing.have ?? 0})` +
                (Number(ing.have_ground ?? 0) > 0 ? ` (+${ing.have_ground} on ground)` : ''),
            }),
          ),
        );
      }
      root.append(rr);
    }

    // tool chip
    const tool = data.tool;
    if (tool && typeof tool === 'object') {
      const missing = !!tool.missing;
      const toolText = tool.item != null ? vsj.infoName(String(tool.item)) : String(tool.type ?? 'tool');
      const trow = el('div', { style: 'display:flex;gap:3px;align-items:center' });
      if (tool.item != null) trow.append(icons.texture(String(tool.item), 16));
      const tp = el('div', {
        style:
          'align-self:flex-start;background:var(--bg-slot);border-radius:3px;padding:2px 5px;' +
          `border:1px solid ${missing ? 'var(--danger)' : 'var(--border)'}`,
      });
      tp.append(trow);
      if (missing) {
        // "Need to [add <tool> to toolbag]" — the button is always rendered;
        // the server decides availability (equip btn or null + equip_blocked
        // deny prose), the client only displays it.
        trow.append(el('span', { class: 'k-small', style: 'color:var(--danger)', text: 'Need to' }));
        const eq = kbtn(tool.equip ?? null, {
          label: `add ${toolText} to toolbag`,
          compact: true,
          denyReason: tool.equip_blocked != null ? String(tool.equip_blocked) : null,
        });
        tooltip(eq, () =>
          tool.equip ? `Put your ${toolText} in the toolbag` : String(tool.equip_blocked ?? ''),
        );
        trow.append(eq);
      } else {
        trow.append(el('span', { class: 'k-small', style: 'color:var(--text-dim)', text: toolText }));
      }
      const dura = tool.durability;
      if (dura && Number(dura.max) > 0) {
        const frac = Math.max(0, Math.min(1, Number(dura.cur) / Number(dura.max)));
        tp.append(
          el(
            'div',
            { class: 'underbar' + (frac < 0.25 ? ' low' : ''), style: 'position:static;margin-top:2px' },
            el('div', { style: `width:${frac * 100}%` }),
          ),
        );
      }
      root.append(tp);
    }

    // finds table (gather/fish)
    const finds = data.finds;
    if (finds && typeof finds === 'object') {
      root.append(el('div', { class: 'k-small k-faint', text: 'Finds' }));
      for (const k of Object.keys(finds).sort()) {
        if (k === '_nothing_pct') continue;
        const f = finds[k];
        if (!f || typeof f !== 'object') continue;
        const row = el(
          'div',
          { style: 'display:flex;gap:4px;align-items:center' },
          icons.texture(k, 16),
          el('span', { class: 'k-small k-dim', style: 'flex:1', text: vsj.infoName(k) }),
          el('span', { class: 'k-small', text: `${f.pct ?? '?'}%` }),
        );
        // served per-find xp
        if (f.xp != null)
          row.append(el('span', { class: 'k-small k-faint', text: `+${f.xp} xp` }));
        if (f.locked_at != null)
          row.append(el('span', { class: 'k-chip', style: 'border-color:var(--warn)', text: `Lv ${f.locked_at}` }));
        root.append(row);
      }
      const nothing = Number(finds._nothing_pct ?? 0);
      if (nothing > 0)
        root.append(el('div', { class: 'k-small k-faint', text: `∅ ${nothing}% finds nothing` }));
    }

    // hunt dens — names click through to the creature's info sheet
    if (Array.isArray(data.dens)) {
      for (const d of data.dens) {
        if (!d || typeof d !== 'object') continue;
        const dn = el('span', {
          class: 'k-small c-creature',
          style: 'cursor:pointer',
          dataset: { info: String(d.info ?? '') },
          text: String(d.name ?? '?'),
        });
        if (d.info) dn.addEventListener('click', () => ui.select(String(d.info)));
        const row = el('div', { style: 'display:flex;gap:4px;align-items:center' }, dn, icons.tierBadge(String(d.tier ?? '')));
        // served pack size {min,max} — this den stands up a whole line
        if (d.pack && typeof d.pack === 'object' && d.pack.min != null) {
          const pk = String(d.pack.min) === String(d.pack.max ?? d.pack.min)
            ? `×${d.pack.min}` : `×${d.pack.min}–${d.pack.max}`;
          const pc = el('span', { class: 'k-chip', text: pk });
          tooltip(pc, () => 'Pack — they attack together');
          row.append(pc);
        }
        if (d.pct != null) row.append(el('span', { class: 'k-small k-dim', style: 'margin-left:auto', text: `${d.pct}%` }));
        root.append(row);
        // the full combat block for the den's creature — statvocab chips off
        // its served infos sheet (already registered by the driver)
        const ci = vsj.info(String(d.info ?? '')) ?? {};
        const sr = el('div', { class: 'stat-row' });
        if (ci.hp != null) sr.append(statChip('hp', ci.hp));
        if (ci.dmg != null) sr.append(statChip('dmg', ci.dmg));
        if (ci.swing_s != null) sr.append(statChip('swt', `${ci.swing_s}s`));
        if (ci.acc != null) sr.append(statChip('acc', ci.acc));
        if (ci.armor != null) sr.append(statChip('arm', ci.armor));
        if (ci.resist != null) sr.append(statChip('res', ci.resist));
        if (ci.agi != null) sr.append(statChip('agi', ci.agi));
        if (ci.speed_yd_s != null) sr.append(statChip('spd', ci.speed_yd_s));
        if (sr.childElementCount) root.append(sr);
      }
    }

    // quick buttons: ∞ and 1
    const reason = data.blocked != null ? String(data.blocked) : null;
    const infBtn = kbtn(data.start_inf, {
      label: '∞',
      denyReason: reason,
      glow: glow.includes('card:' + key) && glow.includes('btn:start') && !glow.includes('btn:craft_one'),
    });
    infBtn.style.flex = '1';
    tooltip(infBtn, () => 'Repeat until stopped');
    const oneBtn = kbtn(data.start_one, {
      label: '1',
      denyReason: reason,
      glow: glow.includes('card:' + key) && glow.includes('btn:craft_one'),
    });
    oneBtn.style.minWidth = '36px';
    tooltip(oneBtn, () => 'Do it once');
    root.append(el('div', { style: 'display:flex;gap:6px' }, infBtn, oneBtn));
  };

  return {
    root,
    update(pKey, pData, glow) {
      key = pKey;
      data = pData ?? {};
      const wantGlow = glow.includes('card:' + key);
      const s =
        JSON.stringify(pData) +
        (wantGlow ? '!g' : '') +
        (glow.includes('btn:start') ? '!s' : '') +
        (glow.includes('btn:craft_one') ? '!1' : '');
      if (s === sig) return;
      sig = s;
      root.classList.toggle('glow', wantGlow);
      clear(root);
      if (data.locked && typeof data.locked === 'object') buildLocked();
      else buildFace(glow);
    },
  };
}
